import mux from 'mux-embed'
import React, {
 useCallback, useEffect, useRef, useState
} from 'react'
import { useLocation, useNavigate } from 'react-router'

import dayjs from 'dayjs'
import Player, {
  PLAYER_ERROR_CODES,
  PLAYER_REPORTABLE_EVENTS,
} from '../../../../shared/components/VideoPlayer'
import {
  ComcastErrorType,
  getComcastErrorTypeFromPlayerError,
  getTTSDuration,
} from '../../../../shared/helpers'
import { useAppDispatch, useAppSelector } from '../../hooks/useStore'
import tts from '../../lib/TTS'
import usePlayerNetworkRecovery from '../../hooks/usePlayerNetworkRecovery'
import Platform from '../../platforms/index'
import { useResolveUrsInApp } from '../../videoPlayer/hooks'
import Spinner from '../Spinner/SpinnerTS'
import { useWatchProgress } from './hooks/useWatchProgress'
import PlayerControls from './PlayerControls/PlayerControls'
import { buildMezzanineUrl, buildMuxData } from './utils'
import { useVideoPlayerData } from './VideoPlayerContext'
import type { VideoPlayer as TVideoPlayer } from '../../../../shared/components/VideoPlayer/types'
import { useNetworkMonitor } from '../../providers/NetworkMonitorProvider'
// import type { VideoPlayer as VideoPlayerTypes } from '../../../../shared/components/VideoPlayer/types'
import { KeyTypeEnum } from '../../../../shared/hooks/useKeyDownHandler'
import { useKeyDown } from '../../../../shared/providers/KeyDownProvider/KeyDownProvider'
import ContinueWatchingModal from '../ContinueWatchingModal/ContinueWatchingModal'
import { USER_INACTIVITY_TIMEOUT_MS } from '../ContinueWatchingModal/types'
import UpNext from '../UpNext/UpNext'
import AdContainer from './adContainer/AdContainer'
import { useUpNext } from './hooks/useGetUpNext'
import type { PlayerEventData, VideoPlayerProps } from './types'
import { PlayerType } from './types'
import useMediaEvent, { VOD } from './useMediaEvent'

const PLAYER_DOM_ID = 'video-player-id'

function VideoPlayer({ initialPlayheadPosition, adsEnabled, loadingAdsFeatureFlag }: VideoPlayerProps) {
  const dispatch = useAppDispatch()
  const device = useAppSelector((state) => state.device)
  const videoDetails = useAppSelector((state) => state.content.videoDetails)
  const { userPid } = useAppSelector((state) => ({
    userPid: state.user.pid,
  }))
  const navigate = useNavigate()
  const playerType = videoDetails.drmWidevideLicenseUrl
    ? PlayerType.DRM
    : PlayerType.NO_DRM
  const mezzanine = buildMezzanineUrl(videoDetails.assetMezzanine)
  const playerRef = useRef<TVideoPlayer | null>(null)
  const [captionsAvailable, setCaptionsAvailable] = useState(false)

  const location = useLocation()
  const [isRebuffering, setIsRebuffering] = useState(false)
  const [isSeekingViaPointer, setIsSeekingViaPointer] = useState(false)

  const shouldResolveUrl = Platform.shouldResolveUrsUrlInApp()
  const { resolvedUrl, isResolving } = useResolveUrsInApp(
    videoDetails.url || '',
    {
      enabled: shouldResolveUrl && !!videoDetails.url,
    }
  )
  const {
    playerState,
    playerStateRef,
    setPlayerStatus,
    setCurrentProgress,
    setDuration,
    setShowControls,
  } = useVideoPlayerData()
  const showSpinner =
    isRebuffering ||
    playerState.status === 'init' ||
    isSeekingViaPointer ||
    isResolving ||
    loadingAdsFeatureFlag
  const { sendMediaStartEvent } = useMediaEvent(VOD)
  const isAdPlaying =
    playerState.status === 'adPlaying' || playerState.status === 'adPaused'
  const playerElementRef = useRef<HTMLVideoElement>(null)
  const [monitoredElement, setMonitoredElement] = useState<{
    id: string
  } | null>(null)
  const emitMuxEvent = useCallback(
    (eventName: string, eventData?: Record<string, unknown>) => {
      if (monitoredElement) {
        mux.emit(monitoredElement.id, eventName, eventData)
      }
    },
    [monitoredElement]
  )
  const hasAnnouncedExit = useRef(false)
  const { isDisconnected, setSuppressAutoErrorModal } = useNetworkMonitor()
  const prevResolvedUrlRef = useRef<string>()

  // Handles network error recovery for playback and ads-to-content transitions
  const { setHadNetworkError } = usePlayerNetworkRecovery({
    playerRef,
    videoElementRef: playerElementRef,
    videoUrl: videoDetails.url || '',
  })

  const [showContinueWatchingModal, setShowContinueWatchingModal] =
    useState(false)
  useUpNext()
  const { updateProgress } = useWatchProgress(
    videoDetails.videoId,
    () => playerState.currentProgress
  )

  const handleFatalError = ({ code, data }) => {
    const errorType = getComcastErrorTypeFromPlayerError(
      code,
      playerType === PlayerType.NO_DRM,
      data
    )
    const isNetworkError = errorType === ComcastErrorType.network
    if (isNetworkError) {
      // Set flag to indicate we had a network error
      // Recovery will be attempted in useEffect when connectionRecovered becomes true
      // Note: Auto error modal is already suppressed by the isDisconnected useEffect
      setHadNetworkError(true)
    } else {
      // For non-network errors, navigate to error page as before
      dispatch({
        type: 'TRIGGER_ERROR',
        payload: {
          status: code,
          code: data.type,
          content: {
            errorPath: location.pathname,
          },
          error: data,
          codeBaseLocation: 'VideoPlayer - handleFatalError',
          errorType,
        },
      })
      navigate('/error', { replace: true })
    }
  }

  const reportPlayerEvent = (
    eventName: string,
    eventData: PlayerEventData = {}
  ): void => {
    const {
 bitrate, width, height, code, message, data
} = eventData
    switch (eventName) {
      case PLAYER_REPORTABLE_EVENTS.RENDITION_CHANGE:
        emitMuxEvent('renditionchange', {
          video_source_bitrate: bitrate,
          video_source_width: width,
          video_source_height: height,
        })
        break
      case PLAYER_REPORTABLE_EVENTS.REBUFFER_START:
        setIsRebuffering(true)
        break
      case PLAYER_REPORTABLE_EVENTS.BUFFER_STALLED:
        // seek(this.state.playheadPosition + 0.5)
        break
      case PLAYER_REPORTABLE_EVENTS.BUFFER_DEPLETED:
        setSuppressAutoErrorModal(false)
        break
      case PLAYER_REPORTABLE_EVENTS.BUFFER_RECOVERED:
        setSuppressAutoErrorModal(true)
        // Note: Don't clear hadNetworkError here - let the recovery hook handle it
        break
      case PLAYER_REPORTABLE_EVENTS.ERROR:
        emitMuxEvent('error', {
          player_error_code: code,
          player_error_message: message,
          player_error_context: data,
        })
        handleFatalError({ code, data })
        break
      default:
        break
    }
  }

  useEffect(() => updateProgress, [])

  useEffect(() => {
    if (isResolving) return undefined

    // Only recreate player if URL actually changed (avoid destroying during recovery/re-resolution)
    if (prevResolvedUrlRef.current !== undefined && 
        prevResolvedUrlRef.current === resolvedUrl) {
      return undefined
    }

    prevResolvedUrlRef.current = resolvedUrl

    setCurrentProgress(initialPlayheadPosition)
    setDuration(videoDetails.duration)
    dispatch({ type: 'UNSET_UPNEXT_DATA' })

    const contentlUrl = shouldResolveUrl ? resolvedUrl : videoDetails.url
    const playerProps = {
      contentSourceUrl: !adsEnabled ? contentlUrl : null,
      reportPlayerEvent,
      setCaptionsAvailable,
      drmWidevideLicenseUrl: videoDetails.drmWidevideLicenseUrl,
    }
    const newPlayer =
      playerType === PlayerType.NO_DRM
        ? Platform.createVODPlayer(playerElementRef, playerProps)
        : Platform.createVODDRMPlayer(playerElementRef, playerProps)

    playerRef.current = newPlayer
    if (playerElementRef.current) {
      const muxMonitor = mux.monitor(playerElementRef.current, {
        debug: false,
        beaconCollectionDomain: 'qoe.pbs.org',
        data: buildMuxData(
          videoDetails,
          userPid,

          device.deviceModel,
          playerRef.current?.getVersion() || ''
        ),
      })
      setMonitoredElement(muxMonitor)
      emitMuxEvent('viewstart')
    }

    newPlayer?.init()

    return () => {
      if (monitoredElement) {
        emitMuxEvent('viewend')
        mux.destroyMonitor(monitoredElement.id)
      }
      playerRef.current?.destroy()
    }
  }, [isResolving, resolvedUrl])

  useEffect(() => {
    if (showSpinner && playerStateRef.current.showControls) {
      setShowControls(false)
    }
  }, [showSpinner, setShowControls, playerStateRef])

  useKeyDown(KeyTypeEnum.Back, () => {
    tts.pushTextToRead('Exiting video playback.')
    hasAnnouncedExit.current = true
  })

  const handlePlaying = () => {
    setPlayerStatus('playing')
    sendMediaStartEvent()
    setIsRebuffering(false)
    setIsSeekingViaPointer(false)
    Platform.startPlayback()
  }

  const handlePaused = () => {
    setPlayerStatus('paused')
  }

  const checkEnd = (currentTime: number) =>
    videoDetails.duration > 0 &&
    (currentTime > videoDetails.duration ||
      Math.abs(videoDetails.duration - currentTime) < 0.3)


  const exitPlayback = () => {
    const { upNextData, lastUserInteraction } = playerStateRef.current
    const upNextVisible = upNextData !== null && upNextData.isUpNextVisible

    if (upNextVisible) {
      const inactiveDuration = dayjs().diff(lastUserInteraction, 'milliseconds')

      if (inactiveDuration >= USER_INACTIVITY_TIMEOUT_MS) {
        setShowContinueWatchingModal(true)
      } else {
        navigate(
          `/video-${
            upNextData?.isMvod && !upNextData?.isPlayable
              ? 'details'
              : 'playback'
          }/${upNextData?.upNextUrl}`,
          { replace: true, state: { fromUpNext: true } }
        )
      }
    } else {
      Platform.endPlayback()
      if (!hasAnnouncedExit.current) {
        tts.pushTextToRead('Exiting video playback.')
        hasAnnouncedExit.current = true
      }
      const windowUrl = location.pathname.split('/')[2]
      navigate(`/video-details/${windowUrl}`, { replace: true })
    }
  }

  const handleEnded = () => {
    setPlayerStatus('ended')
    exitPlayback()
  }

  const handleTimeUpdate = (currentTime: number) => {
    if (checkEnd(currentTime) && playerStateRef.current.status !== 'ended') {
      handleEnded()
    }
    setCurrentProgress(currentTime)
  }

  const playOrPause = () => {
    switch (playerState.status) {
      case 'playing':
        tts.addTextToRead(
          `Paused at ${getTTSDuration(playerState.currentProgress)}`
        )
        playerRef.current?.pause()
        break
      case 'paused':
        tts.addTextToRead(
          `Playing from ${getTTSDuration(playerState.currentProgress)}`
        )
        playerRef.current?.play()
        break
      case 'adPlaying':
        tts.addTextToRead(
          `Paused at ${getTTSDuration(playerState.adData?.adCurrentTime)}`
        )
        setPlayerStatus('adPaused')
        break
      case 'adPaused':
        tts.addTextToRead(
          `Playing from ${getTTSDuration(playerState.adData?.adCurrentTime)}`
        )
        setPlayerStatus('adPlaying')
        break
      default:
        break
    }
  }

  const backToPlayingContent = () => {
    setPlayerStatus('init')
    setIsRebuffering(true)
    setDuration(videoDetails.duration)
    setCurrentProgress(initialPlayheadPosition)

    if (adsEnabled && videoDetails.url) {
      // If offline, show error and let recovery hook handle load when reconnected
      if (isDisconnected) {
        setHadNetworkError(true)
        setSuppressAutoErrorModal(false)
        handleFatalError({
          code: PLAYER_ERROR_CODES.NETWORK,
          data: {
            type: 'network',
            message: 'Network unavailable when loading content',
          },
        })
        return
      }

      playerRef.current?.load(videoDetails.url)
    }
  }

  const handleSeek = (seekTime: number) => {
    setPlayerStatus('seeked')
    setCurrentProgress(seekTime)
    playerRef.current?.seek(seekTime)
    emitMuxEvent('seeked')
    playerRef.current?.play()
    updateProgress()
    tts.addTextToRead(`Playing from ${getTTSDuration(seekTime)}`)
  }

  const handleLoadedData = () => {
    if (initialPlayheadPosition > 0) {
      playerRef.current?.seek(initialPlayheadPosition)
    }
  }

  const shouldEnableUpNext =
    playerState.upNextData !== null &&
    !isAdPlaying &&
    playerState.status !== 'init'

  const closeContinueWatchingModal = () => {
    setShowContinueWatchingModal(false)
  }

  const navigateToUpNextContent = () => {
    const { upNextData } = playerStateRef.current
    const pathType =
      upNextData?.isMvod && !upNextData?.isPlayable ? 'details' : 'playback'
    navigate(`/video-${pathType}/${upNextData?.upNextUrl}`, {
      replace: true,
      state: {
        fromContinueWatchingModal: true,
      },
    })
  }

  const navigateToVideoDetails = () => {
    const windowUrl = window.location.href.split('/')[4]
    navigate(`/video-details/${windowUrl}`, { replace: true })
  }

  const handleContinueWatchingNavigation = (continueWatching: boolean) => {
    closeContinueWatchingModal()
    if (continueWatching) {
      navigateToUpNextContent()
    } else {
      navigateToVideoDetails()
    }
  }

  return (
    <div
      id="video-player-container"
      aria-hidden
      className={
        playerState.showControls || playerState.isUserTryingToSeek
          ? 'controls-visible'
          : ''
      }
    >
      {showContinueWatchingModal ? (
        <ContinueWatchingModal
          handleContinueWatchingNavigation={handleContinueWatchingNavigation}
        />
      ) : (
        <>
          <div
            className="video-player-mezzanine"
            style={{
              backgroundImage: mezzanine,
              visibility:
                playerState.status === 'init' || playerState.status === 'loaded'
                  ? 'visible'
                  : 'hidden',
            }}
          />
          {showSpinner && !isAdPlaying && <Spinner />}
          <PlayerControls
            captionsAvailable={captionsAvailable && !isAdPlaying}
            playOrPause={playOrPause}
            seek={handleSeek}
            pause={() => playerRef.current?.pause()}
            play={() => playerRef.current?.play()}
            controlsSuppressed={showSpinner}
            setIsSeekingViaPointer={setIsSeekingViaPointer}
          />
          <Player
            ref={playerElementRef}
            onLoadedData={handleLoadedData}
            onPlaying={handlePlaying}
            onPaused={handlePaused}
            onTimeUpdate={handleTimeUpdate}
            onEnded={handleEnded}
            onError={() => {
              // Error handled by reportPlayerEvent
            }}
            captionsAvailable={captionsAvailable}
            domID={PLAYER_DOM_ID}
          />
          {adsEnabled && (
            <AdContainer
              playerRef={playerElementRef}
              pause={() => playerRef.current?.pause()}
              emitMuxEvent={emitMuxEvent}
              backToPlayingContent={backToPlayingContent}
            />
          )}
          {shouldEnableUpNext && <UpNext />}
        </>
      )}
    </div>
  )
}

export default VideoPlayer
